import React, { useState, useCallback, useEffect } from 'react';
import { isDemoOnPhone } from '@/demo/demoDataWindow';

interface ChartDateLimiterProps {
  labels: string[];
  datasets: any[];
  children: (limitedLabels: string[], limitedDatasets: any[]) => React.ReactNode;
  yearLimit?: number;
}

/**
 * Wraps a chart with a dual-handle range slider for narrowing the visible date window.
 * Defaults to showing the last N years of data. Only renders the slider when data
 * exceeds yearLimit * 12 points.
 */
const ChartDateLimiter: React.FC<ChartDateLimiterProps> = ({
  labels,
  datasets,
  children,
  yearLimit = 2,
}) => {
  const pointsPerYear = 12;
  // Demo on a phone opens at one year; two years of points is unreadable that narrow.
  const limit = (isDemoOnPhone() ? 1 : yearLimit) * pointsPerYear;
  const needsSlider = labels.length > limit;

  const defaultStart = needsSlider ? labels.length - limit : 0;
  const defaultEnd = labels.length - 1;

  const [rangeStart, setRangeStart] = useState(defaultStart);
  const [rangeEnd, setRangeEnd] = useState(defaultEnd);

  useEffect(() => {
    setRangeStart(defaultStart);
    setRangeEnd(defaultEnd);
  }, [labels.length]);

  const handleStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (val < rangeEnd) setRangeStart(val);
  }, [rangeEnd]);

  const handleEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (val > rangeStart) setRangeEnd(val);
  }, [rangeStart]);

  const handleReset = useCallback(() => {
    setRangeStart(defaultStart);
    setRangeEnd(defaultEnd);
  }, [defaultStart, defaultEnd]);

  const limitedLabels = labels.slice(rangeStart, rangeEnd + 1);
  const limitedDatasets = datasets.map((ds) => {
    const sliced: any = {
      ...ds,
      data: (ds.data ?? []).slice(rangeStart, rangeEnd + 1),
    };
    if (Array.isArray(ds.backgroundColor)) {
      sliced.backgroundColor = ds.backgroundColor.slice(rangeStart, rangeEnd + 1);
    }
    if (Array.isArray(ds.borderColor)) {
      sliced.borderColor = ds.borderColor.slice(rangeStart, rangeEnd + 1);
    }
    return sliced;
  });

  const isDefault = rangeStart === defaultStart && rangeEnd === defaultEnd;

  // Compute track fill percentages
  const total = labels.length - 1;
  const leftPct = total > 0 ? (rangeStart / total) * 100 : 0;
  const rightPct = total > 0 ? (rangeEnd / total) * 100 : 100;

  return (
    <div className="flex flex-col gap-2">
      {children(limitedLabels, limitedDatasets)}
      {needsSlider && (
        <div className="flex flex-col gap-1 pt-1" data-testid="chart-date-limiter-slider">
          {/* Track + dual range inputs */}
          <div className="relative h-4 flex items-center">
            {/* Background track */}
            <div className="absolute inset-x-0 h-1 bg-rs-jet-light rounded-full" />
            {/* Active range highlight */}
            <div
              className="absolute h-1 bg-rs-green/50 rounded-full"
              style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
            />
            {/* Start handle */}
            <input
              type="range"
              min={0}
              max={labels.length - 1}
              value={rangeStart}
              onChange={handleStartChange}
              data-testid="chart-date-limiter-start"
              className="absolute inset-0 z-20 w-full appearance-none bg-transparent pointer-events-none
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-3
                [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-rs-green
                [&::-webkit-slider-thumb]:border-0
                [&::-webkit-slider-thumb]:pointer-events-auto
                [&::-webkit-slider-thumb]:cursor-ew-resize
                [&::-webkit-slider-runnable-track]:bg-transparent
                [&::-moz-range-thumb]:w-3
                [&::-moz-range-thumb]:h-3
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-rs-green
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:pointer-events-auto
                [&::-moz-range-thumb]:cursor-ew-resize"
            />
            {/* End handle */}
            <input
              type="range"
              min={0}
              max={labels.length - 1}
              value={rangeEnd}
              onChange={handleEndChange}
              data-testid="chart-date-limiter-end"
              className="absolute inset-0 z-30 w-full appearance-none bg-transparent pointer-events-none
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-3
                [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-rs-green
                [&::-webkit-slider-thumb]:border-0
                [&::-webkit-slider-thumb]:pointer-events-auto
                [&::-webkit-slider-thumb]:cursor-ew-resize
                [&::-webkit-slider-runnable-track]:bg-transparent
                [&::-moz-range-thumb]:w-3
                [&::-moz-range-thumb]:h-3
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-rs-green
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:pointer-events-auto
                [&::-moz-range-thumb]:cursor-ew-resize"
            />
          </div>
          {/* Labels row */}
          <div className="flex items-center justify-between">
            <span className="text-rs-off-white/50 text-sm">{labels[rangeStart]}</span>
            <div className="flex items-center gap-2">
              {!isDefault && (
                <button
                  onClick={handleReset}
                  data-testid="chart-date-limiter-reset"
                  className="text-rs-off-white/40 hover:text-rs-off-white/70 text-sm transition-colors"
                >
                  Reset
                </button>
              )}
            </div>
            <span className="text-rs-off-white/50 text-sm">{labels[rangeEnd]}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartDateLimiter;
